import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatSidenavModule } from '@angular/material/sidenav';
import { HangForm } from '@components/forms/hang/hang-form.component';
import {
  HangDrawerWidth,
  HangFormOverlayType,
  HangFormView,
} from '@components/forms/hang/hang-form.types';
import { urlSignal } from '@utils/signal-utils';
import { Hang, HangResourceName } from 'pueblo-types';
import { PageLayout } from 'types/ui-types';
import { HangFormOverlayStore } from '../hang-form-overlay.store';

const VALID_URL_PATTERNS = [
  /hang\/.*?\/edit(\?.*)?$/,
  /hang\/create(\?.*)?$/,
  /tabs\/hangs(\?.*)?$/,
];

@Component({
  selector: 'hang-drawer',
  templateUrl: 'hang-drawer.component.html',
  styleUrl: 'hang-drawer.component.scss',
  imports: [HangForm, MatIconModule, MatSidenavModule, NgClass],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HangDrawer {
  protected readonly store = inject(HangFormOverlayStore);

  protected readonly PageLayout = PageLayout;
  protected readonly HangFormOverlayType = HangFormOverlayType;

  readonly cancel = output<void>();
  readonly complete = output<Hang>();
  readonly delete = output<HangResourceName>();

  protected readonly $hangForm = viewChild.required<HangForm>('hangForm');

  protected readonly $formView = signal<HangFormView>(HangFormView.MAIN);
  protected readonly $formPendingHang = signal<Hang | undefined>(undefined);

  protected readonly $url = urlSignal();

  protected readonly $visible = computed(
    () => this.store.visible() && this.store.overlayType() === 'drawer',
  );

  constructor() {
    effect(() => {
      const formView = this.$formView();
      untracked(() => {
        switch (formView) {
          case HangFormView.MAIN:
            this.store.setDrawerWidth(HangDrawerWidth.HALF);
            break;
          case HangFormView.DETAILS:
            this.store.setDrawerWidth(HangDrawerWidth.THREE_QUARTER);
            break;
          case HangFormView.NEW_PUEBLO:
            this.store.setDrawerWidth(HangDrawerWidth.THREE_QUARTER);
            break;
        }
      });
    });

    effect(() => {
      const formPendingHang = this.$formPendingHang();
      const visible = this.store.visible();
      untracked(() => {
        if (formPendingHang && visible) {
          this.store.setPendingHang(formPendingHang);
        }
      });
    });

    effect(() => {
      const url = this.$url();
      untracked(() => {
        if (
          this.store.visible() &&
          !VALID_URL_PATTERNS.some((pattern) => pattern.test(url))
        ) {
          this.store.close(false);
        }
      });
    });
  }

  protected handleCreated(hang: Hang) {
    this.complete.emit(hang);
    this.store.close();
  }

  protected handleUpdated(hang: Hang) {
    this.complete.emit(hang);
    this.store.close();
  }

  protected handleCancel() {
    this.cancel.emit();
    this.store.close();
  }
}
